import {
  convertDateRangeToFilterValue,
  deserializeDateRangeOrLookback,
  formattedDateForSubscription,
  isLookbackWindow,
  type BlotterTableFilter,
  type DateRangeOrLookback,
  type LookbackWindow,
} from '@talos/kyoko';
import type { SpaceParameterPanelValueBinding } from './types';

export type SpaceDateRangeValue =
  | LookbackWindow
  | {
      from: string | null;
      to: string | null;
    };

export const SPACE_DATE_RANGE_FILTER_KEYS = ['_start', 'StartDate', 'EndDate'] as const;

export function serializeSpaceDateRangeValue(value: DateRangeOrLookback): SpaceDateRangeValue {
  if (isLookbackWindow(value)) {
    return value;
  }
  return {
    from: formattedDateForSubscription(value.from) ?? null,
    to: formattedDateForSubscription(value.to) ?? null,
  };
}

export function deserializeSpaceDateRangeValue(value: SpaceDateRangeValue): DateRangeOrLookback {
  if (!isLookbackWindow(value) && value.from == null && value.to == null) {
    return { from: null, to: null };
  }

  return deserializeDateRangeOrLookback(value);
}

export function isSpaceDateRangeValue(value: unknown): value is SpaceDateRangeValue {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  if (isLookbackWindow(value as DateRangeOrLookback)) {
    return true;
  }
  const { from, to } = value as { from?: unknown; to?: unknown };
  return (from == null || typeof from === 'string') && (to == null || typeof to === 'string');
}

export function applySpaceDateRangeValue(filter: BlotterTableFilter, value: unknown): BlotterTableFilter {
  if (!isSpaceDateRangeValue(value)) {
    return filter;
  }
  if (isLookbackWindow(value)) {
    return convertDateRangeToFilterValue(value, filter);
  }

  const nextFilter = { ...filter };
  delete nextFilter._start;
  if (value.from == null) {
    delete nextFilter.StartDate;
  } else {
    nextFilter.StartDate = value.from;
  }
  if (value.to == null) {
    delete nextFilter.EndDate;
  } else {
    nextFilter.EndDate = value.to;
  }
  return nextFilter;
}

export const SPACE_DATE_RANGE_PANEL_VALUES = {
  panelValueKeys: SPACE_DATE_RANGE_FILTER_KEYS,
  parameterValueToPanelValues: (value, currentPanelValues) => applySpaceDateRangeValue(currentPanelValues, value),
} satisfies SpaceParameterPanelValueBinding;
